/**
 * @file api.js
 * @module utils/api
 *
 * ## System Context
 * Central Axios instance used by every React component to communicate with
 * the FastAPI backend.  This is the single place where transport-level
 * cross-cutting concerns (cookie auth, silent token refresh, session expiry
 * signalling) are configured.  No component should import `axios` directly.
 *
 * ## Design Rationale — C2 (Cycle-02 HttpOnly Cookie Migration)
 * Prior to C2, the access token lived in `localStorage` and was manually
 * attached to every request via an `Authorization: Bearer` header.
 * localStorage is readable by any JavaScript in the same origin, making it
 * a target for XSS-based token theft.
 *
 * After C2:
 *   1. The backend sets `fv_access_token` and `fv_refresh_token` as HttpOnly
 *      cookies on login.  JavaScript cannot read these cookies at all.
 *   2. `withCredentials: true` tells Axios (and the browser) to send those
 *      cookies automatically on every same-origin request.
 *   3. When a request returns 401 (expired access token), the interceptor
 *      silently calls `POST /api/auth/refresh` — which uses the refresh
 *      cookie to issue a new access cookie — then retries the original
 *      request once.
 *   4. If the refresh call also returns 401 (both tokens expired / revoked),
 *      all queued requests are rejected and the custom event
 *      `fv:session-expired` is dispatched so AuthContext.js can clear user
 *      state and redirect to the login screen.
 *
 * ## Interceptor Queue Pattern
 * Multiple concurrent requests may 401 at the same moment (e.g., on app
 * startup when the access token just expired).  Without a queue, each would
 * independently attempt a refresh, racing each other.  Instead:
 *   - The first 401 sets `_isRefreshing = true` and starts one refresh call.
 *   - Subsequent 401s while refreshing are pushed onto `_refreshQueue` as
 *     deferred promises.
 *   - On refresh success, `_processQueue(null)` resolves all queued promises
 *     and each original request is retried once.
 *   - On refresh failure, `_processQueue(error)` rejects all queued promises
 *     and the session-expired event is dispatched.
 *
 * ## External Dependencies
 *   - axios                            : HTTP client
 *   - Backend POST /api/auth/refresh   : Token rotation endpoint (C2)
 *   - CustomEvent 'fv:session-expired' : Consumed by AuthContext.js
 */
import axios from 'axios';
import BACKEND_URL from './backendUrl';
import i18n from '../i18n';



// NOTE: baseURL includes /api so all callers use short paths like
// `api.get('/auth/me')` rather than repeating the full origin every time.
const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  // C2: withCredentials=true sends the HttpOnly auth cookies on every request.
  // The access token is NO LONGER read from localStorage.
  withCredentials: true,
});

// The UI language goes with every request so the server can answer messages in it.
// Read from storage (not the i18n module) to avoid a circular import.
api.interceptors.request.use(config => {
  try {
    const lang = localStorage.getItem('fv-lang');
    if (lang) config.headers['Accept-Language'] = lang;
  } catch { /* storage blocked: server answers in English */ }
  // a request the gateway never answers ends with a message instead of a spinner that never stops (QA FVL-030):
  // 60 s, uploads 5 minutes; a caller can set its own
  if (!config.timeout) config.timeout = (typeof FormData !== 'undefined' && config.data instanceof FormData) ? 300000 : 60000;
  return config;
});


// ── Session Gate Flags ────────────────────────────────────────────────────────
// These flags prevent the interceptor from entering an infinite cascade of
// 401 → refresh → 401 → refresh... cycles during logout or after session expiry.
//
// _sessionExpired: set to true after the FIRST refresh attempt fails.
//   Once true, all subsequent 401s are swallowed silently — no more DB queries,
//   no more refresh calls, no more React error overlay.
//
// _isLoggingOut: set to true by AuthContext BEFORE calling POST /api/auth/logout.
//   When active, any in-flight request that gets a 401 is absorbed immediately
//   (no refresh attempted) so component teardown 401s never reach the overlay.
//
// resetSessionState(): called by AuthContext on every successful login to
//   re-arm the interceptor for the new session.

let _isRefreshing   = false;
let _refreshQueue   = [];    // { resolve, reject } pairs for queued requests.
let _sessionExpired = false; // once true, no further refresh is attempted.
let _isLoggingOut   = false; // set by AuthContext during POST /api/auth/logout.

/** Set during POST /api/auth/logout to absorb in-flight 401s silently. */
export const setLogoutMode = (val) => {
  _isLoggingOut = val;
  // Also arm the session-expired gate so any post-logout 401s never retry.
  if (val) _sessionExpired = true;
};

/** Re-arm the interceptor after a successful login. Called by AuthContext. */
export const resetSessionState = () => {
  _sessionExpired = false;
  _isLoggingOut   = false;
  _isRefreshing   = false;
  _refreshQueue   = [];
};

// ── Refresh-on-401 Interceptor ────────────────────────────────────────────────

/**
 * Resolve or reject all queued requests after a refresh attempt completes.
 * @param {Error|null} error - null on success, Error instance on failure
 */
const _processQueue = (error) => {
  _refreshQueue.forEach(({ resolve, reject }) =>
    error ? reject(error) : resolve()
  );
  _refreshQueue = [];
};

// Server messages with values in them: [pattern, 'errors' key with {{placeholders}}]
const ERROR_PATTERNS = [
  [/^Sync failed again: (?<error>.+)$/, 'Sync failed again: {{error}}'],
  [/^(?<count>\d+) organisations use (?<name>.+) — move them to another package first$/, '{{count}} organisations use {{name}} — move them to another package first'],
  [/^A (?<audience>dealer|lco) package named (?<name>.+) already exists$/, 'A package with this name already exists: {{name}}'],
  [/^(?<name>.+) is not a top-level dealer group$/, '{{name}} is not a top-level dealer group'],
  [/^Unknown features: (?<list>.+)$/, 'Unknown features: {{list}}'],
];

// One refresh call, tried a second time after a short wait when the server could not answer (restart, 429, timeout)
const _refreshWithRetry = async () => {
  const call = () => axios.post(`${BACKEND_URL}/api/auth/refresh`, {}, { withCredentials: true });
  try {
    return await call();
  } catch (err) {
    const st = err.response?.status;
    if (st === 401 || st === 403) throw err;
    await new Promise(r => setTimeout(r, 1500));
    return call();
  }
};

api.interceptors.response.use(
  // Pass successful responses straight through — no processing needed.
  (response) => response,

  async (error) => {
    const originalRequest = error.config;
    const status          = error.response?.status;

    const reqUrl = originalRequest?.url || '';
    const isAuthCredentialCall = /\/auth\/(login|verify-code|resend-code|verify-mfa|forgot-password|reset-password)/.test(reqUrl);

    // ── Gate: already logging out or session already dead ─────────────────────
    // Swallow any 401 silently — do NOT attempt refresh, do NOT dispatch
    // session-expired again, do NOT propagate to React's error overlay.
    // This is the core fix for the "logout → red screen" bug:
    //   After cookies are cleared, in-flight component requests get 401.
    //   Without this gate every one of them would independently try to refresh,
    //   fail, and throw an uncaught rejection that React's overlay catches.
    // Exception: credential endpoints must keep their response body so the login
    // form can show "Invalid credentials" / lockout messages after a logout.
    if (status === 401 && (_isLoggingOut || _sessionExpired) && !isAuthCredentialCall) {
      // Return a resolved-but-empty response so awaiting callers get `null`
      // data rather than throwing. Components are teardown-safe at this point.
      return Promise.reject(Object.assign(new Error('Session ended'), { fvSessionEnded: true }));
    }

    // Only attempt silent refresh for 401s on non-auth endpoints.
    // Retrying /auth/refresh or /auth/login on 401 would cause an infinite loop.
    if (
      status === 401 &&
      !originalRequest._retried &&
      !isAuthCredentialCall &&
      !reqUrl.includes('/auth/refresh')
    ) {
      if (_isRefreshing) {
        // A refresh is already in flight — defer this request until it resolves.
        return new Promise((resolve, reject) => {
          _refreshQueue.push({ resolve, reject });
        })
          .then(() => api(originalRequest))
          .catch((err) => Promise.reject(err));
      }

      // Mark this request as retried to prevent a second 401 from re-entering
      // the interceptor (which would cause an infinite refresh loop).
      originalRequest._retried = true;
      _isRefreshing             = true;

      try {
        // Silent token refresh — the browser sends the refresh cookie automatically.
        await _refreshWithRetry();
        // Refresh succeeded: unblock all queued requests then retry this one.
        _processQueue(null);
        return api(originalRequest);

      } catch (refreshError) {
        // Only a refused refresh (401 / 403) ends the session. A server restart (502 / 503), a timeout or the
        // refresh rate limit (429) is not a reason to sign a working user out (QA DP-03): this request fails
        // and the next one tries again.
        const refreshStatus = refreshError.response?.status;
        if (refreshStatus !== 401 && refreshStatus !== 403) {
          _processQueue(refreshError);
          return Promise.reject(error);
        }
        // ── CRITICAL: set _sessionExpired BEFORE _processQueue ───────────────
        // This ensures that when _processQueue rejects all waiting promises,
        // those promises' .catch() handlers won't re-enter the interceptor and
        // attempt another refresh.  Without this flag, every waiting request
        // would each independently retry → another cascade of refresh calls.
        _sessionExpired = true;
        _processQueue(refreshError);
        window.dispatchEvent(new CustomEvent('fv:session-expired'));
        // Reject with a recognizable marker so callers can distinguish a
        // session-expiry rejection from a real API error.
        const expiredErr = Object.assign(new Error('Session expired'), { fvSessionExpired: true });
        return Promise.reject(expiredErr);

      } finally {
        _isRefreshing = false;
      }
    }

    // Refused by the organisation role (backend services/role_policy.py): show it in the page language
    const denied = status === 403 && error.response?.headers?.['x-fv-denied'];
    // a feature outside the organisation's licence package (backend services/licence.py, QA AD-01)
    const unlicensed = status === 403 && error.response?.headers?.['x-fv-unlicensed'];
    if (denied && error.response.data) {
      error.response.data.detail = i18n.t(`common:role_denied_${denied}`, { defaultValue: error.response.data.detail });
    } else if (unlicensed && error.response.data) {
      const feature = i18n.t(`common:licence_feature_${unlicensed}`, { defaultValue: unlicensed });
      error.response.data.detail = i18n.t('common:licence_feature_not_included', {
        defaultValue: error.response.data.detail, feature: `${feature} (${unlicensed})` });
    } else if (typeof error.response?.data?.detail === 'string') {
      // Server messages are English sentences; the 'errors' translations hold them in the page language
      const detail = error.response.data.detail;
      // messages carrying a name or a number are matched by their pattern (QA FV-035)
      const hit = ERROR_PATTERNS.find(([re]) => re.test(detail));
      error.response.data.detail = hit
        ? i18n.t(hit[1], { ns: 'errors', nsSeparator: false, keySeparator: false, defaultValue: detail, ...detail.match(hit[0]).groups })
        : i18n.t(detail, { ns: 'errors', nsSeparator: false, keySeparator: false, defaultValue: detail });
    } else if (!axios.isCancel(error) && (!error.response || [502, 503, 504].includes(status))) {
      // no answer, or the gateway's: a message every page's error text shows, so a failed action never stays silent (QA FVL-030)
      const detail = error.response
        ? i18n.t('common:server_busy', { defaultValue: 'The service is busy right now. Wait a moment and try again.' })
        : error.code === 'ECONNABORTED'
        ? i18n.t('common:server_timeout', { defaultValue: 'The server took too long to answer. Try again in a moment.' })
        : i18n.t('common:server_unreachable', { defaultValue: 'The server could not be reached. Check your connection and try again.' });
      error.response = { ...(error.response || { status: 0, headers: {} }), data: { detail } };
    }

    return Promise.reject(error);
  },
);

export default api;
